/**
 * Writing what an importer read as a chapter's stored text (part 18 log, principles 1 and 2, tables I9 and R). An
 * importer describes a chapter as draft blocks of runs (text with its marks, breaks, note references, images); this
 * module builds part 04's model from them, so every character is literal text and the one canonical writer escapes
 * it, then parses what it wrote and checks it reads back as meant. Pure.
 */
import { parseChapter } from '../parser/parse.ts';
import type { Inline, TextRun } from '../literal/inline.ts';
import { chapterModel } from '../literal/model.ts';
import type { Block, Centered, Footnote, Heading, List, Paragraph } from '../literal/model.ts';
import { OutputLimitError, serializeChapter } from '../literal/serialize.ts';
import { modelShape } from '../literal/shape.ts';
import { ConversionError } from './problems.ts';

/** The inline formatting the book has (decision 35): I9. */
export interface Marks {
  readonly italic: boolean;
  readonly bold: boolean;
  readonly smallCaps: boolean;
}

/** No formatting. */
export const PLAIN_MARKS: Marks = Object.freeze({ italic: false, bold: false, smallCaps: false });

/** Part 23a, RI3, RI7: where a re-import's text came from: its character style's name (null: none). */
export interface RunSource {
  readonly style: string | null;
}

/** One piece of a leaf's content as an importer read it. */
export type Run =
  | { readonly kind: 'text'; readonly text: string; readonly marks: Marks; readonly href: string | null; readonly source?: RunSource }
  | { readonly kind: 'break' }
  /**
   * A note reference: `key` names the note in the chapter's notes; `mark` its custom mark (CM2), when it has one; `href`
   * the link it stands in (part 23a, RI9: the export splits a link at a reference).
   */
  | { readonly kind: 'note'; readonly key: string; readonly mark?: string; readonly href?: string | null }
  /**
   * `name`, `descr`: part 23a's RI5, RI8b, the picture's `wp:docPr` name and description, kept by re-import; `href` the
   * link around it (RI9, C″16b).
   */
  | { readonly kind: 'image'; readonly alt: string; readonly destination: string; readonly name?: string; readonly descr?: string; readonly href?: string | null };

/** A block of text: a paragraph, a heading or a centered line. */
export type DraftLeaf =
  | { readonly kind: 'paragraph' | 'centered'; readonly runs: readonly Run[] }
  /** A level past 6 is written as 6 (B5). */
  | { readonly kind: 'heading'; readonly level: number; readonly runs: readonly Run[] };

/** A block as an importer read it. */
export type DraftBlock =
  | DraftLeaf
  | { readonly kind: 'scene-break' }
  | { readonly kind: 'quote'; readonly blocks: readonly DraftBlock[] }
  /** `loose`: part 23a's RC4, the list's looseness as the file gives it (loose anyway where CommonMark needs it). */
  | { readonly kind: 'list'; readonly start: number | null; readonly items: readonly (readonly DraftBlock[])[]; readonly loose?: boolean };

/** A chapter as written, and what had to give way on the way (R2, R4). */
export interface WrittenChapter {
  readonly text: string;
  /** R2: leaves whose inline formatting didn't read back and was left out. */
  readonly formattingKeptOut: number;
  /** R4: the chapter written as plain paragraphs. */
  readonly structureLost: boolean;
}

/** A run's text as a literal piece. */
function textRun(text: string): TextRun {
  return { kind: 'text', pieces: [{ text, from: 0, to: 0, origin: 'literal' }] };
}

/** I3: line ends in a run's text (XML lets `w:t` hold them; Word shows each as a space), and form feeds. */
// eslint-disable-next-line no-control-regex -- the vertical tab is one of them; `\v` would do, but Stryker's regex mutator makes it `\V`, which no `u` pattern parses
const LINE_ENDS = /[\n\r\x0b\f\u0085\u2028\u2029]/gu;

/**
 * The leaf's runs as lines: line ends inside a text a space (a line of text is never split but by a break), white space
 * trimmed at each line's edges, empty texts gone, no break at an edge or twice; with `breaks` (part 23a, the export's
 * own hard breaks), every break kept but at the end, which Markdown can't hold.
 */
function tidy(runs: readonly Run[], breaks: boolean): Run[] {
  let line: Run[] = [];
  const lines = [line];
  for (const run of runs) {
    if (run.kind === 'break') lines.push((line = []));
    else line.push(run.kind === 'text' ? { ...run, text: run.text.replace(LINE_ENDS, ' ') } : run);
  }
  const kept = lines.map(trimLine);
  if (breaks) {
    while (kept.at(-1)?.length === 0) kept.pop();
    return kept.flatMap((line, i) => (i === 0 ? line : [{ kind: 'break' } as const, ...line]));
  }
  const out: Run[] = [];
  for (const trimmed of kept) {
    if (trimmed.length === 0) continue;
    if (out.length > 0) out.push({ kind: 'break' });
    for (const each of trimmed) out.push(each);
  }
  return out;
}

/**
 * `text` with its trailing spaces and tabs taken off, by one scan from the end. Fix-next 85 (the security audit of
 * 2026-11-02, run 2): `/[\t ]+$/u` retries from every space of a long run that ends in another character, quadratic.
 */
export function withoutTrailingBlanks(text: string): string {
  let end = text.length;
  while (end > 0 && (text[end - 1] === ' ' || text[end - 1] === '\t')) end -= 1;
  return text.slice(0, end);
}

/** One line's runs with the white space at its two edges trimmed and empty texts dropped. */
function trimLine(line: readonly Run[]): Run[] {
  const out = line.filter((run) => run.kind !== 'text' || run.text.length > 0);
  // The run at `index` trimmed at its start (`start`) or its end, and the next one in when nothing is left of it.
  const edge = (index: number, start: boolean): void => {
    const run = out[index];
    if (run?.kind !== 'text') return;
    const text = start ? run.text.replace(/^[\t ]+/u, '') : withoutTrailingBlanks(run.text);
    if (text === '') {
      out.splice(index, 1);
      edge(start ? index : index - 1, start);
    } else out[index] = { ...run, text };
  };
  edge(0, true);
  edge(out.length - 1, false);
  return out;
}

/** A mark of {@link Marks}. */
type Mark = keyof Marks;

const SPAN_OF = { bold: ['strong', '**'], italic: ['emphasis', '*'], smallCaps: ['small-caps', '^^'] } as const;

/** Consecutive items of `items` with the same key, in order. */
function groupBy<T, K>(items: readonly T[], keyOf: (item: T) => K): { readonly key: K; readonly runs: T[] }[] {
  const out: { key: K; runs: T[] }[] = [];
  for (const item of items) {
    const key = keyOf(item);
    const last = out.at(-1);
    if (last?.key === key) last.runs.push(item);
    else out.push({ key, runs: [item] });
  }
  return out;
}

/** A draft's text run. */
type TextDraft = Extract<Run, { kind: 'text' }>;

/** Consecutive runs with `mark` (texts only) and without it, in order. */
function stretches(runs: readonly Run[], mark: Mark): ({ readonly marked: true; readonly runs: TextDraft[] } | { readonly marked: false; readonly runs: Run[] })[] {
  const out: ({ readonly marked: true; readonly runs: TextDraft[] } | { readonly marked: false; readonly runs: Run[] })[] = [];
  for (const run of runs) {
    const last = out.at(-1);
    if (run.kind === 'text' && run.marks[mark]) {
      if (last?.marked === true) last.runs.push(run);
      else out.push({ marked: true, runs: [run] });
    } else if (last?.marked === false) last.runs.push(run);
    else out.push({ marked: false, runs: [run] });
  }
  return out;
}

/**
 * I9: a marked stretch's texts with the white space at its two edges taken off (across runs that hold only white
 * space), to stand outside its span, where it can't break the delimiters' flanking.
 */
function peel(texts: readonly TextDraft[]): { readonly lead: string; readonly inner: TextDraft[]; readonly trail: string } {
  const runs = texts.map((run) => ({ ...run }));
  let lead = '';
  for (const run of runs) {
    const kept = run.text.trimStart();
    lead += run.text.slice(0, run.text.length - kept.length);
    run.text = kept;
    if (kept !== '') break;
  }
  let trail = '';
  for (const run of [...runs].reverse()) {
    const kept = run.text.trimEnd();
    trail = run.text.slice(kept.length) + trail;
    run.text = kept;
    if (kept !== '') break;
  }
  return { lead, inner: runs.filter((run) => run.text !== ''), trail };
}

/**
 * I9: runs of one link (or none) as inline content, the marks in `marks` as spans nested in that order: emphasis ›
 * strong › small caps, as `***a***` reads (measured: on prose a fixed order reads back where the longest-outermost
 * choice often doesn't).
 */
function spans(runs: readonly Run[], marks: readonly Mark[]): Inline[] {
  const [mark, ...inner] = marks;
  if (mark === undefined) return runs.flatMap(leafInline);
  const out: Inline[] = [];
  for (const stretch of stretches(runs, mark)) {
    if (!stretch.marked) {
      for (const each of spans(stretch.runs, inner)) out.push(each);
      continue;
    }
    const { lead, inner: kept, trail } = peel(stretch.runs);
    if (lead !== '') out.push(textRun(lead));
    if (kept.length > 0) {
      const [kind, delimiter] = SPAN_OF[mark];
      out.push({ kind, open: delimiter, close: delimiter, children: spans(kept, inner), from: 0, to: 0 });
    }
    if (trail !== '') out.push(textRun(trail));
  }
  return join(out);
}

/** A run with no formatting left to apply, as inline content. */
function leafInline(run: Run): Inline[] {
  switch (run.kind) {
    case 'text':
      // Never empty: tidy and peel drop empty texts.
      return [textRun(run.text)];
    case 'break':
      return [{ kind: 'hard-break', from: 0, to: 0, spaces: false }];
    case 'note':
      return [{ kind: 'footnote-reference', label: run.key, from: 0, to: 0 }];
    case 'image':
      // Part 23a, RI5: an export's picture may have no alt; an empty text is written as nothing and has no shape.
      return [{ kind: 'image', destination: run.destination, title: null, children: [textRun(run.alt)], from: 0, to: 0, textEnd: 0 }];
  }
}

/** Neighbouring text runs joined: one run per stretch of text. */
function join(content: readonly Inline[]): Inline[] {
  const out: Inline[] = [];
  for (const item of content) {
    const last = out.at(-1);
    if (item.kind === 'text' && last?.kind === 'text') out[out.length - 1] = textRun([...last.pieces, ...item.pieces].map((piece) => piece.text).join(''));
    else out.push(item);
  }
  return out;
}

/** How much of a leaf's formatting is written (R2): which marks, and whether links. */
interface Level {
  readonly marks: readonly Mark[];
  readonly links: boolean;
}

/**
 * R2: the levels a leaf is tried at, in order: everything; then a mark at a time left out, the less used in a novel first
 * (italics last); then links alone.
 */
const LEVELS: readonly Level[] = [
  { marks: ['italic', 'bold', 'smallCaps'], links: true },
  { marks: ['italic', 'bold'], links: true },
  { marks: ['italic', 'smallCaps'], links: true },
  { marks: ['italic'], links: true },
  { marks: ['bold'], links: true },
  { marks: [], links: true },
];

/** R2's last resort: no marks, no links. */
const PLAINEST: Level = { marks: [], links: false };

/** A leaf's runs as inline content at `level`: links grouped, then spans inside them. */
function inlineOf(runs: readonly Run[], kind: DraftLeaf['kind'], level: Level, labels: ReadonlyMap<string, string>, breaks: boolean): Inline[] {
  // N4: references to notes that have no label go before the lines are tidied, so no break is left at an edge.
  const labelled = runs.flatMap((run): Run[] => {
    if (run.kind !== 'note') return [run];
    const label = labels.get(run.key);
    return label === undefined ? [] : [{ kind: 'note', key: label }];
  });
  const tidied = tidy(labelled, breaks)
    .flatMap((run): Run[] => {
      // B18: a heading or centered line is one line; at its plainest a break is a space.
      if (!level.links && run.kind === 'break' && kind !== 'paragraph') return [{ kind: 'text', text: ' ', marks: PLAIN_MARKS, href: null }];
      return [run];
    });
  const out: Inline[] = [];
  // A link holds its texts, and (part 23a, RI9) the pictures read inside it, and a break or note reference standing between
  // two of its runs (the export writes a break inside the link and splits the link at a reference, C″14, C″19).
  // No link at all at a level without links.
  const own = tidied.map((run) => (run.kind === 'text' || run.kind === 'image' ? (level.links ? (run.href ?? null) : null) : undefined));
  // The nearest own link before and after each run, one pass each way (CO).
  let seen: string | null | undefined;
  const nearest = (href: string | null | undefined): string | null | undefined => {
    const was = seen;
    if (href !== undefined) seen = href;
    return was;
  };
  const before = own.map(nearest);
  seen = undefined;
  const after = [...own].reverse().map(nearest).reverse();
  // A run of no own link between two runs of one link (or none) is in it; undefined, as at an edge, is none.
  const hrefs = own.map((href, i): string | null | undefined => (href !== undefined ? href : breaks && before[i] === after[i] ? before[i] : null));
  for (const { key: href, runs: group } of groupBy(tidied.map((run, i) => ({ run, href: hrefs[i] ?? null })), (each) => each.href).map(({ key, runs }) => ({ key, runs: runs.map((each) => each.run) }))) {
    const content = spans(group, level.marks);
    if (href === null) for (const each of content) out.push(each);
    else out.push({ kind: 'link', destination: href, title: null, children: content, from: 0, to: 0, textEnd: 0 });
  }
  return join(out);
}

/** S1 (part 10b): an empty paragraph, a paragraph holding one no-break space. */
const EMPTY: readonly Inline[] = [textRun('\u00a0')];

/** B5: a heading's level as Markdown has it: 1 to 6, deeper levels 6. */
function headingLevel(level: number): Heading['level'] {
  if (level <= 1) return 1;
  if (level === 2) return 2;
  if (level === 3) return 3;
  if (level === 4) return 4;
  return level === 5 ? 5 : 6;
}

/** A leaf as a model block at `level`; an empty leaf is an empty paragraph. */
function leafBlock(leaf: DraftLeaf, level: Level, labels: ReadonlyMap<string, string>, breaks: boolean): Paragraph | Heading | Centered {
  const content = inlineOf(leaf.runs, leaf.kind, level, labels, breaks);
  const filled = content.length === 0 ? EMPTY : content;
  if (leaf.kind === 'heading') return { kind: 'heading', level: headingLevel(leaf.level), content: filled, from: 0, to: 0 };
  return { kind: leaf.kind === 'centered' && content.length > 0 ? 'centered' : 'paragraph', content: filled, from: 0, to: 0 };
}

/**
 * R2: whether `block` written alone reads back as itself. Its note references need no definitions beside it: the
 * grammar reads `[^label]` as a reference whatever the chapter defines (part 03).
 */
function readsBack(block: Block): boolean {
  const blocks = [block];
  const text = serializeChapter({ blocks });
  return modelShape(chapterModel(parseChapter(text)).blocks) === modelShape(blocks);
}

/** A chapter's draft and its notes, with the labels its references get, written as model blocks. */
class Builder {
  formattingKeptOut = 0;
  readonly labels = new Map<string, string>();
  /** The notes referenced, in label order. */
  readonly order: (readonly DraftBlock[])[] = [];

  readonly #notes: ReadonlyMap<string, readonly DraftBlock[]>;
  /**
   * Part 23a: writing what re-import read of an export, which encodes what Markdown holds: a note's references to the
   * chapter's notes kept (else dropped, N6, RN12), an empty note left empty (RN4), hard breaks kept (RI1).
   */
  readonly #reimport: boolean;

  constructor(notes: ReadonlyMap<string, readonly DraftBlock[]>, reimport = false) {
    this.#notes = notes;
    this.#reimport = reimport;
  }

  /**
   * N2, N3: labels in order of first reference, counting from 1; a reference to no note gets none (N4). Part 23a, RN12:
   * then the references inside the notes labelled, in their order, so a note only a note cites (chained) gets one too.
   */
  label(blocks: readonly DraftBlock[]): void {
    this.#label(blocks);
    // The loop meets the notes it labels as they are added.
    if (this.#reimport) for (const note of this.order) this.#label(note);
  }

  #label(blocks: readonly DraftBlock[]): void {
    for (const block of blocks) {
      if (block.kind === 'quote') this.#label(block.blocks);
      else if (block.kind === 'list') for (const item of block.items) this.#label(item);
      else if (block.kind !== 'scene-break') {
        for (const run of block.runs) {
          if (run.kind !== 'note' || this.labels.has(run.key)) continue;
          const note = this.#notes.get(run.key);
          if (note === undefined) continue;
          this.labels.set(run.key, String(this.labels.size + 1));
          this.order.push(note);
        }
      }
    }
  }

  /**
   * R2: a leaf at the most formatting that reads back, trying each of {@link LEVELS}; failing every one, its text, notes,
   * pictures and breaks alone ({@link PLAINEST}), which the chapter's own read-back (R3) checks with the rest.
   */
  leaf(leaf: DraftLeaf, labels: ReadonlyMap<string, string>): Block {
    for (const [i, level] of LEVELS.entries()) {
      const block = leafBlock(leaf, level, labels, this.#reimport);
      if (!readsBack(block)) continue;
      if (i > 0) this.formattingKeptOut += 1;
      return block;
    }
    this.formattingKeptOut += 1;
    return leafBlock(leaf, PLAINEST, labels, this.#reimport);
  }

  blocks(blocks: readonly DraftBlock[], labels: ReadonlyMap<string, string> = this.labels): Block[] {
    return blocks.map((block): Block => {
      switch (block.kind) {
        case 'scene-break':
          return { kind: 'scene-break', from: 0, to: 0 };
        case 'quote':
          return { kind: 'quote', blocks: this.blocks(block.blocks, labels), from: 0, to: 0 };
        case 'list': {
          // CommonMark: an item holding two blocks with a blank line between them makes its list loose. A list right
          // after the item's text needs none, unless it is numbered from other than 1 or opens with an empty item
          // (part 23a, deep-fix r5), neither of which can interrupt a paragraph.
          const needs = block.items.some((item) => item.some((inner, i) => i > 0 && (inner.kind !== 'list' || (inner.start ?? 1) !== 1 || inner.items[0]?.length === 0)));
          const list: List = { kind: 'list', start: block.start, loose: block.loose ?? needs, items: block.items.map((item) => this.blocks(item, labels)), itemEnds: block.items.map(() => 0), from: 0, to: 0 };
          // Part 23a, RC4: a list the file says is tight stays tight where it reads back so (a quote can follow an
          // item's text with no blank line), else loose.
          return block.loose === false && needs && !readsBack(list) ? { ...list, loose: true } : list;
        }
        case 'paragraph':
        case 'heading':
        case 'centered':
          return this.leaf(block, labels);
      }
    });
  }

  /**
   * N2, N5: the definitions, in label order, their own references dropped (N6); part 23a's notes hold any block, and
   * re-importing, their references to the chapter's notes are labelled (RN12).
   */
  definitions(): Footnote[] {
    const labels = this.#reimport ? this.labels : new Map<string, string>();
    return this.order.map((note, i): Footnote => {
      const blocks = this.blocks(note, labels);
      // N5: part 18's empty note is an empty paragraph; part 23a's (RN4: the export's `[^n]:`) stays empty.
      return { kind: 'footnote', label: String(i + 1), blocks: blocks.length === 0 && !this.#reimport ? [{ kind: 'paragraph', content: EMPTY, from: 0, to: 0 }] : blocks, from: 0, to: 0 };
    });
  }
}

/** Every leaf of `blocks`, in order, deepest included. */
function leavesOf(blocks: readonly DraftBlock[]): DraftLeaf[] {
  return blocks.flatMap((block): DraftLeaf[] => {
    if (block.kind === 'quote') return leavesOf(block.blocks);
    if (block.kind === 'list') return block.items.flatMap(leavesOf);
    return block.kind === 'scene-break' ? [] : [block];
  });
}

/**
 * Table R: `blocks`, with the notes their references name, as a chapter's canonical stored text. `limit` bounds the
 * text (table CO); past it, a ConversionError `expands`. With `reimport` (part 23a), what an export encodes is kept: the
 * references inside notes, empty notes and hard breaks.
 */
export function writeChapter(blocks: readonly DraftBlock[], notes: ReadonlyMap<string, readonly DraftBlock[]>, limit: number, reimport = false): WrittenChapter {
  const builder = new Builder(notes, reimport);
  builder.label(blocks);
  const model = [...builder.blocks(blocks), ...builder.definitions()];
  const text = serialize(model, limit);
  // R3.
  if (modelShape(chapterModel(parseChapter(text)).blocks) === modelShape(model)) return { text, formattingKeptOut: builder.formattingKeptOut, structureLost: false };
  // R4: plain paragraphs, the notes' text after the chapter's; a builder that labels nothing drops every reference (N4).
  const plain = new Builder(new Map());
  const leaves = [...leavesOf(blocks), ...builder.order.flatMap(leavesOf)].map((leaf): DraftLeaf => ({ kind: 'paragraph', runs: leaf.runs }));
  return { text: serialize(plain.blocks(leaves), limit), formattingKeptOut: builder.formattingKeptOut, structureLost: true };
}

function serialize(blocks: readonly Block[], limit: number): string {
  try {
    return serializeChapter({ blocks }, limit);
  } catch (error) {
    if (error instanceof OutputLimitError) throw new ConversionError('expands', 'the chapter would be far longer than its file');
    throw error;
  }
}

/** The text of `runs` as a title: texts joined, breaks as spaces, white space collapsed and trimmed (C2). */
export function runsTitle(runs: readonly Run[]): string {
  return runs
    .map((run) => (run.kind === 'text' ? run.text : run.kind === 'break' ? ' ' : ''))
    .join('')
    .replace(/\s+/gu, ' ')
    .trim();
}

/** Whether a leaf holds nothing the writer would see (B10): no text but white space, no image. */
export function isBlankLeaf(leaf: DraftLeaf): boolean {
  return leaf.runs.every((run) => run.kind === 'break' || (run.kind === 'text' && run.text.trim() === ''));
}
